// ────────────────────────────────────────────────────────────────────────────
// The document's type scale.
//
// Everything used to hang off one body size with hardcoded offsets baked into
// the renderer: labels at base-1, the organisation name at base+4, the grand
// total at base+2. Those offsets are reasonable defaults and useless as
// constraints — a long trading name needs a smaller letterhead, a forty-line
// invoice needs a denser table than its totals, and some organisations want the
// amount due to shout.
//
// So each size is an optional override that falls back to its offset. Left
// alone, a template prints exactly as it did before and the sizes still track
// the body size; set one and it is pinned. That is why they are optional rather
// than absolute numbers written into every layout: a saved template keeps
// following the body size until someone deliberately breaks the link.
// ────────────────────────────────────────────────────────────────────────────
import type { ThemeSettings } from './types';

export interface ResolvedTypeScale {
  /** Body copy, table rows unless overridden, and everything unspecified. */
  body: number;
  /** Section labels (BILL TO, NOTES), the footer, and page numbers. */
  small: number;
  /** The organisation name at the top — the letterhead line. */
  organisationName: number;
  /** The grand-total row. */
  total: number;
  /** The line table, header and rows. */
  line: number;
}

/** Smallest size worth printing; below this it is decoration, not text. */
const MIN_PT = 5;

export function resolveTypeScale(theme: ThemeSettings): ResolvedTypeScale {
  const body = theme.baseFontSizePt;
  return {
    body,
    small: theme.smallFontSizePt ?? Math.max(MIN_PT, body - 1),
    organisationName: theme.organisationNameSizePt ?? body + 4,
    total: theme.totalFontSizePt ?? body + 2,
    line: theme.lineFontSizePt ?? body,
  };
}

/** The keys the inspector can pin, and how each is labelled. */
export const TYPE_SCALE_FIELDS = [
  'smallFontSizePt',
  'organisationNameSizePt',
  'totalFontSizePt',
  'lineFontSizePt',
] as const;
export type TypeScaleField = (typeof TYPE_SCALE_FIELDS)[number];

export const TYPE_SCALE_LABEL: Record<TypeScaleField, string> = {
  smallFontSizePt: 'Labels & footer',
  organisationNameSizePt: 'Organisation name',
  totalFontSizePt: 'Total row',
  lineFontSizePt: 'Line table',
};

/** Which resolved size each override corresponds to, for showing the default. */
export const TYPE_SCALE_TARGET: Record<TypeScaleField, keyof ResolvedTypeScale> = {
  smallFontSizePt: 'small',
  organisationNameSizePt: 'organisationName',
  totalFontSizePt: 'total',
  lineFontSizePt: 'line',
};
